import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";

interface FileNameProps {
  name: string;
  onRename: (name: string) => void;
}

/**
 * The open file's path as breadcrumbs (folder › name); click to rename or move it inline (Enter
 * commits, Escape cancels).
 */
export function FileName({ name, onRename }: FileNameProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  if (!editing) {
    return (
      <button
        type="button"
        className="file-name"
        title="Rename"
        onClick={() => {
          setDraft(name);
          setEditing(true);
        }}
      >
        {name.split("/").map((part, i, parts) =>
          i < parts.length - 1 ? (
            // biome-ignore lint/suspicious/noArrayIndexKey: segments of one path
            <span key={i} className="crumb">
              {part}
              <Icon name="chevron" size={12} />
            </span>
          ) : (
            <span key="file" className="crumb crumb-file">
              {part}
            </span>
          ),
        )}
      </button>
    );
  }

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== name) onRename(next);
  };

  return (
    <input
      ref={inputRef}
      aria-label="File name"
      className="file-name-input"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") setEditing(false);
      }}
    />
  );
}
